import { Badge, Button, Form, ListGroupItem, OverlayTrigger, Placeholder, Popover, Tooltip } from "react-bootstrap";
import { FhirPatientVisit } from "../../amplify/appsync/fetch-sync-fhir";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/appsync/search-patients';

import { faStar as faStarOutline } from "@fortawesome/free-regular-svg-icons";
import { faChevronDown, faChevronUp, faClockRotateLeft, faExclamationTriangle, faPerson, faPlus, faPlusCircle, faSpinner, faStar, faVideo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Favorite } from "../../amplify/appsync/query";
import { callWrite, DELETE_FAVORITE, DELETE_FOLLOWED_PATIENT, INSERT_FAVORITE, INSERT_FOLLOWED_PATIENT } from "../../amplify/appsync/write";
import { DISPO_RISK_THRESHOLD } from "../../utils/plot";
import { getHourMinuteDifference, getLatestTime, getLocalDateStrFromDate, getLocalTimeStrFromDate } from "../../utils/time";
import PersonalIdentifier from "../common/PersonalIdentifier";
import { SectionQuickView } from "./llm/SectionQuickView";
import React from "react";
import { LLMDashboardMarkdownRenderer } from "../dashboard/llm/common/LLMDashboardMarkdownRenderer";


export interface TrackboardV2ItemProps {
    patient: PatientVisit & FhirPatientVisit & PatientVisitMetadata,
    pastFavorites?: Favorite[],
    pastReviewed?: Favorite[],
    updatePatientWithNote: any,
    csnToFavorites: any,
    csnToReviewed: any,
    setCsnToReviewed: any,
    columns: any[],
    setShowContextMenu: any,
    setContextMenu: any,
    tenant: string,
    padNotes?: boolean,
    isPlaceholder?: boolean,
    user?: any,
    ws?: any,
}


function areColumnsEqual(prevCols: any[], nextCols: any[]) {
  if (prevCols === nextCols) return true; // same reference
  if (prevCols.length !== nextCols.length) return false;

  for (let i = 0; i < prevCols.length; i++) {
    // shallow compare each column
    const prevCol = prevCols[i];
    const nextCol = nextCols[i];
    if (prevCol.id !== nextCol.id) {
      return false;
    }
  }
  return true;
}

// Note that React.memo is used because when the names are being loaded dynamically,
// if the item is expanded, it will cause the LLM objects to reload constantly.
export const TrackboardV2Item = React.memo((props: TrackboardV2ItemProps) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [localFollow, setLocalFollow] = useState(false);

    const [noteText, setNoteText] = useState(props.patient && props.patient.note ? props.patient.note : "");
    const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
    const [isSavingFollowing, setIsSavingFollowing] = useState(false);
    const [isLoadingReviewed, setIsLoadingReviewed] = useState(false);

    const totalRelativeCols = props.columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);

    const isFavorite = () => {
        if (props.csnToFavorites[props.patient.csn] && props.csnToFavorites[props.patient.csn].length > 0) {
            return true;
        }
        return false;
    }

    const isFollowed = () => {
        return props.patient.auto_follow || props.patient.manual_follow || localFollow;
    }

    const isReviewed = () => {
        if (props.csnToReviewed[props.patient.csn]) {
            return true;
        }
        return false;
    }

    const updateCsnToReviewed = (reviewed?: Favorite) => {
        const map: any = {...props.csnToReviewed};
        if (reviewed) {
            // newly saved favorite
            map[props.patient.csn] = reviewed;
        } else {
            // favorite deleted
            if (map[props.patient.csn]) {
                delete map[props.patient.csn]
            }
        }
        props.setCsnToReviewed(map);
    }

    const saveReviewed = () => {
        setIsLoadingReviewed(true);
        callWrite({
            operation: INSERT_FAVORITE,
            favorite_time: new Date().getTime(),
            favorite_type: "reviewed",
            csn: props.patient.csn,
            mrn: props.patient.mrn,
            internal_csn: props.patient.internal_csn,
            internal_mrn: props.patient.internal_mrn,
        })
        .then((output: Favorite) => {
            console.log("Saved reviewed", output);
            setIsLoadingReviewed(false);
            updateCsnToReviewed(output);
        });
    }

    const deleteReviewed = () => {
        setIsLoadingReviewed(true);
        callWrite({
            operation: DELETE_FAVORITE,
            favorite_type: "reviewed",
            csn: props.patient.csn,
        })
        .then((output: Favorite) => {
            console.log("Deleted reviewed", output);
            // Purposefully don't pass output to update code to indicate deletion
            setIsLoadingReviewed(false);
            updateCsnToReviewed();
        });
    }

    const getWidth = (col: any) => {
        return `${100 * col.size / totalRelativeCols}%`;
    }

    const getAcuity = () => {
        let badgeVariant = "light";
        let className = "";
        if (props.patient.triage_acuity) {
            const val = props.patient.triage_acuity;
            if (val.startsWith("1")) {
                badgeVariant = "danger";
            } else if (val.startsWith("2")) {
                badgeVariant = "warning";
                className = "trackboard-bg-emergent";
            } else if (val.startsWith("3")) {
                badgeVariant = "warning";
                className = "trackboard-bg-urgent";
            } else {
                className = "trackboard-bg-other";
            }
        }
        return <Badge bg={badgeVariant} className={className}>{props.patient.triage_acuity}</Badge>
    }

    const getNumPriorityActions = () => {
        let badgeVariant = "light";
        let className = "";
        if (props.patient && props.patient.num_priority_actions !== undefined) {
            const val = Number(props.patient.num_priority_actions);
            if (val > 0) {
                badgeVariant = "danger";
            } else {
                className = "trackboard-bg-other";
            }
        } else {
            className = "trackboard-bg-other";
        }
        return <Badge bg={badgeVariant} className={className}>{props.patient.num_priority_actions ?? 0}</Badge>
    }

    const getElapsedTime = () => {
        const dt = new Date(props.patient.arrival_time as string);
        let currTime = getLatestTime(props.patient, props.tenant);
        return `${getHourMinuteDifference(dt, new Date(currTime))}`
    }

    const formatSecondsToHHMM = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
    
        const paddedHours = `${hours}`.padStart(2, '0');
        const paddedMinutes = `${minutes}`.padStart(2, '0');
    
        return `${paddedHours}:${paddedMinutes}`;
    };

    const handleFollowPatient = () => {
        setIsSavingFollowing(true);
        if (isFollowed()) {
            callWrite({
                operation: DELETE_FOLLOWED_PATIENT,
                csn: props.patient.csn,
                username: props.user.username,
            })
            .then(() => {
                setLocalFollow(false);
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });
        } else {
            callWrite({
                operation: INSERT_FOLLOWED_PATIENT,
                csn: props.patient.csn,
                username: props.user.username,
                follow_type: "MANUAL",
            })
            .then(() => {
                setLocalFollow(true);
            })
            .finally(() => {
                setIsSavingFollowing(false);
            });
        }
    };

    return (
        <ListGroupItem className="d-flex flex-column trackboard-item">
            <div style={{ display: 'block', width: '100%' }} onClick={() => setIsExpanded(!isExpanded)}>
                {
                    props.columns.map((col: any, idx: number) => {
                        if (props.isPlaceholder) {
                            return (
                                <div key={`placeholder-${idx}`} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={6} size="xs" />
                                    </Placeholder>
                                </div>
                            )
                        }
                        if (col.id === "reviewed") {
                            return (
                                <div key={`reviewed-${col.id}`} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {
                                        isLoadingReviewed ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                        ) : (
                                            <Form.Check
                                                type="checkbox"
                                                checked={isReviewed()}
                                                onClick={(e: any) => {
                                                    e.stopPropagation();
                                                }}
                                                onChange={(e: any) => {
                                                    e.stopPropagation();
                                                    setIsLoadingReviewed(true);
                                                    if (isReviewed()) {
                                                        deleteReviewed();
                                                    } else {
                                                        saveReviewed();
                                                    }
                                                }}
                                            />
                                        )
                                    }
                                </div>
                            )
                        } else if (col.id === "arrival_time") {
                            if (props.patient.arrival_time) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <span>{getLocalDateStrFromDate(new Date(props.patient.arrival_time))} {getLocalTimeStrFromDate(new Date(props.patient.arrival_time))}</span>
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "name") {
                            if (props.patient.given || props.patient.given === "") {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <span><PersonalIdentifier identifier={`${props.patient.family}, ${props.patient.given}`} /></span>
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "languages") {
                            if (props.patient.given || props.patient.given === "") {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {props.patient.languages && props.patient.languages.length > 0 && props.patient.languages.join(", ")}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <Placeholder as="p" animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </div>
                                )
                            }
                        } else if (col.id === "total_mon_time") {
                            if (props.patient.total_mon) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {formatSecondsToHHMM(props.patient.total_mon)}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "has_evaluate_capacity_output") {
                            if (props.patient.has_evaluate_capacity_output) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        YES
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <span>-</span>
                                    </div>
                                )
                            }
                        } else if (col.id === "afib_burden") {
                            if (props.patient.afib_total_sec && props.patient.total_mon) {
                                const percentage = (props.patient.afib_total_sec / props.patient.total_mon) * 100;
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {`${percentage.toFixed(2)}%`}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "longest_afib_episode") {
                            if (props.patient.afib_epi) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        {formatSecondsToHHMM(props.patient.afib_epi)}
                                    </div>
                                )
                            } else {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                       <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Data will be available once patient is discharged
                                                </Tooltip>
                                            }
                                        >
                                            <span>-</span>
                                        </OverlayTrigger>
                                    </div>
                                )
                            }
                        } else if (col.id === "triage_acuity") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {getAcuity()}
                                </div>
                            )
                        } else if (col.id === "num_priority_actions") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {getNumPriorityActions()}
                                </div>
                            )
                        } else if (col.id === "suitability") {
                            const suitableVertical = props.patient.suitable_vertical;
                            const verticalJustification = props.patient.vertical_justification;
                            const suitableVideo = props.patient.suitable_video;
                            const videoJustification = props.patient.video_justification;

                            const getSuitabilityBadge = (suitability: string | undefined) => {
                                if (suitability === "yes") return "success";
                                if (suitability === "no") return "danger";
                                if (suitability === "possibly") return "warning";
                                return "secondary";
                            };

                            const getSuitabilityText = (suitability: string | undefined) => {
                                if (suitability === "yes") return "Yes";
                                if (suitability === "no") return "No";
                                if (suitability === "possibly") return "Maybe";
                                return "-";
                            };

                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {suitableVertical && (suitableVertical === "yes" || suitableVertical === "no" || suitableVertical === "possibly") && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Popover id={`popover-vertical-${props.patient.csn}`}>
                                                    <Popover.Header as="h3">Vertical Eligibility</Popover.Header>
                                                    <Popover.Body>
                                                        <LLMDashboardMarkdownRenderer content={verticalJustification || "No justification provided"} />
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <Badge bg={getSuitabilityBadge(suitableVertical)} className="me-1" style={{ cursor: 'pointer' }}>
                                                <FontAwesomeIcon icon={faPerson} className="me-1" />
                                                {getSuitabilityText(suitableVertical)}
                                            </Badge>
                                        </OverlayTrigger>
                                    )}
                                    {suitableVideo && (suitableVideo === "yes" || suitableVideo === "no" || suitableVideo === "possibly") && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Popover id={`popover-video-${props.patient.csn}`}>
                                                    <Popover.Header as="h3">Video Eligibility</Popover.Header>
                                                    <Popover.Body>
                                                        {videoJustification || "No justification provided"}
                                                    </Popover.Body>
                                                </Popover>
                                            }
                                        >
                                            <Badge bg={getSuitabilityBadge(suitableVideo)} style={{ cursor: 'pointer' }}>
                                                <FontAwesomeIcon icon={faVideo} className="me-1" />
                                                {getSuitabilityText(suitableVideo)}
                                            </Badge>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )
                        } else if (col.id === "elapsed") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {getElapsedTime()}
                                </div>
                            )
                        } else if (col.id === "admit_score") {
                            const isHigh = props.patient.admit_score && Number(props.patient.admit_score) >= DISPO_RISK_THRESHOLD;
                            // Note that at the request of the COMPASS research coordinators, not making it red to avoid confusion with revisit risk
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "" : "" }}>
                                    {props.patient.admit_score !== undefined ?  Number(props.patient.admit_score).toFixed(2) : ""}{' '}
                                    {isHigh && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Patient likely to be admitted (&gt; 0.534)
                                                </Tooltip>
                                            }
                                        >
                                            <span><FontAwesomeIcon icon={faExclamationTriangle} /></span>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_present") {
                            const isHigh = props.patient.afib_present && props.patient.afib_present.length > 0 &&  Number(props.patient.afib_present[0]) >= 0.5;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.afib_present && props.patient.afib_present.length > 0 ? "Y" : "-"}{' '}
                                    {isHigh && (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Afib detected in at least one ECG segment
                                                </Tooltip>
                                            }
                                        >
                                            <span><FontAwesomeIcon icon={faExclamationTriangle} /></span>
                                        </OverlayTrigger>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_no_prev") {
                            // # 0 = No previous data
                            // # 1 = Previous data, no arrhythmia and no anti-coag
                            // # 3 = Previous data, arrhythmia, no anti-coag
                            // # 5 = Previous data, no arrhythmia, anti-coag
                            // # 7 = Previous data, arrhytnmia, anti-coag
                            if (!props.patient.afib_no_prev || props.patient.afib_no_prev.length === 0) {
                                return (
                                    <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                        -
                                    </div>
                                )
                            }

                            const prevCriteriaScore = Number(props.patient.afib_no_prev[0]);
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: prevCriteriaScore === 1 ? "red" : "" }}>
                                    {prevCriteriaScore === 0 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    No past historical data available for patient
                                                </Tooltip>
                                            }
                                        >
                                            <span>No Data</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 1 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    No past arrhythmia and no prior anti-coag med
                                                </Tooltip>
                                            }
                                        >
                                            <span>No History</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 3 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past arrhythmia but no anti-coag
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior Arrhythmia</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 5 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past anti-coagulant but no prior arrhythmia
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior Anti-Coag</span>
                                        </OverlayTrigger>
                                    ) : prevCriteriaScore === 7 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    Past anti-coagulant and prior arrhythmia
                                                </Tooltip>
                                            }
                                        >
                                            <span>Prior History</span>
                                        </OverlayTrigger>
                                    ) : (
                                        <>Unknown</>
                                    )}
                                </div>
                            )
                        } else if (col.id === "afib_cha") {
                            let score = -1;
                            if (props.patient.afib_cha && props.patient.afib_cha.length > 0) {
                                score = Number(props.patient.afib_cha[0]);
                            }
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: score >= 2 ? "red" : "" }}>
                                    {score >= 2 ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip>
                                                    CHA2DS2–VASc &gt;= 2 (anti-coagulant recommended)
                                                </Tooltip>
                                            }
                                        >
                                            <span>{score}</span>
                                        </OverlayTrigger>
                                    ) : (
                                        <span>{score >= 0 ? score : "-"}</span>
                                    )}
                                </div>
                            )
                        } else if (col.id === "epic_revisit_score") {
                            const isHigh = props.patient.epic_revisit_score && props.patient.epic_revisit_score.length > 0 &&  Number(props.patient.epic_revisit_score[0]) >= 0.5;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.epic_revisit_score && props.patient.epic_revisit_score.length > 0 ?  Number(props.patient.epic_revisit_score[0]).toFixed(2) : ""}{' '}
                                    {isHigh && <FontAwesomeIcon icon={faExclamationTriangle} />}
                                </div>
                            )
                        } else if (col.id === "triage_hr") {
                            // @ts-ignore
                            const isHigh = props.patient.triage_hr && props.patient.triage_hr !== "NaN" && props.patient.triage_hr >= 110;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.triage_hr}
                                </div>
                            )
                        } else if (col.id === "triage_spo2") {
                            // @ts-ignore
                            const isHigh = props.patient.triage_spo2 && props.patient.triage_spo2 !== "NaN" && props.patient.triage_spo2 < 90;
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isHigh ? "red" : "" }}>
                                    {props.patient.triage_spo2}
                                </div>
                            )
                        } else if (col.id === "triage_nbp") {
                            // @ts-ignore
                            const sbp = (!props.patient.triage_sbp || props.patient.triage_sbp === "NaN") ? "-" : Math.round(props.patient.triage_sbp);
                            // @ts-ignore
                            const dbp = (!props.patient.triage_dbp || props.patient.triage_dbp === "NaN") ? "-" : Math.round(props.patient.triage_dbp);

                            let isLow = false;
                            // @ts-ignore
                            if (props.patient.triage_sbp && props.patient.triage_sbp !== "NaN" && props.patient.triage_dbp && props.patient.triage_dbp !== "NaN") {
                                isLow =((2 * props.patient.triage_dbp + props.patient.triage_sbp) / 3) < 65;
                            }
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col), color: isLow ? "red" : "" }}>
                                    {sbp}/{dbp}
                                </div>
                            )
                        } else if (col.id === "action") {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    <Button
                                        variant="primary"
                                        className={"trackboard-follow-btn"}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            handleFollowPatient();
                                        }}
                                    >
                                        {
                                            isSavingFollowing ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : isFollowed() ? (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip>
                                                            Unfollow Patient
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span><FontAwesomeIcon icon={faPlusCircle} /></span>
                                                </OverlayTrigger>
                                            ) : (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip>
                                                            Follow Patient
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>
                                                        {/** @ts-ignore */}
                                                        <FontAwesomeIcon icon={faPlus} />
                                                    </span>
                                                </OverlayTrigger>
                                            )
                                        }
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className={"trackboard-save-btn"}
                                        onClick={(e: any) => {
                                            console.log(e)
                                            e.stopPropagation();
                                            props.setShowContextMenu(true);
                                            props.setContextMenu({
                                                x: e.pageX,
                                                y: e.pageY,
                                                patient: props.patient,
                                                listName: null,
                                            })
                                        }}
                                    >
                                        {
                                            isLoadingFavorite ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : isFavorite() ? (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip>
                                                            Remove from Favorites
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span><FontAwesomeIcon icon={faStar} /></span>
                                                </OverlayTrigger>
                                            ) : props.pastFavorites && props.pastFavorites.length > 0 ? (
                                                <>
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip>
                                                                Favorite in previous encounter
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span style={{color: 'red'}}><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                                                    </OverlayTrigger>
                                                </>
                                            ) : (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip>
                                                            Add to Favorites
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span>
                                                        {/** @ts-ignore */}
                                                        <FontAwesomeIcon icon={faStarOutline} />
                                                    </span>
                                                </OverlayTrigger>
                                            )
                                        }
                                    </Button>
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={
                                            <Tooltip>
                                                {isExpanded ? "Collapse" : "Expand"}
                                            </Tooltip>
                                        }
                                    >
                                        <Button
                                            variant="primary"
                                            className="trackboard-expand-btn"
                                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded)}}
                                        >
                                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )
                        } else {
                            return (
                                <div key={col.id} style={{ display: 'inline-block', width: getWidth(col) }}>
                                    {(() => {
                                        // @ts-ignore
                                        const value = props.patient[col.id];

                                        if (!value || value === "NaN") return "-";

                                        if (col.round) {
                                            const num = Number(value);
                                            return isNaN(num) ? value : num.toFixed(col.round);
                                        }

                                        return value;
                                    })()}
                                </div>
                            )
                        }
                    })
                }
            </div>
            {
                !isExpanded && noteText && (
                    <div style={{ display: 'block', width: '100%', cursor: 'default' }}>
                        <div style={{display: 'inline-block'}}>
                            <div style={{ paddingLeft: props.padNotes ? '32px' : '0px', display: 'inline-block', color: '#999' }}>
                                {/** @ts-ignore */}
                                {noteText}
                            </div>
                        </div>
                    </div>
                )
            }

            {isExpanded && !props.isPlaceholder &&
                <div style={{ cursor: 'default', display: 'block', width: '100%', paddingTop: '12px', }}>
                    <SectionQuickView patient={props.patient} updatePatientWithNote={props.updatePatientWithNote} showPatientMetadata={true} user={props.user} tenant={props.tenant} ws={props.ws} />
                </div>
            }
        </ListGroupItem>
    );
}, (prev, next) => {
    const columnsEqual = areColumnsEqual(prev.columns, next.columns);
    const samePatientObject = prev.patient === next.patient;
    const samePatientKeys = prev.patient ? (prev.patient.csn === next.patient.csn &&
                            prev.patient.family === next.patient.family) : false;

    // Only skip refenderring if BOTH columns and patient identity are unchanged
    return columnsEqual && (samePatientObject || samePatientKeys);
});
